import { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef, memo } from "react";
import PropTypes from "prop-types";
import { Pagination, Button } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";

import MuiTable from "./MuiTable";
import CardSkeletonOverlay from "./CardSkeletonOverlay";
import "./ResponsiveGrid.scss";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

// ── CSV export helpers ──────────────────────────────────────────────────
// Escapes a single CSV cell (wraps in quotes when it contains a comma, quote
// or newline; doubles embedded quotes). null/undefined -> empty string.
const escapeCsvCell = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Builds a CSV string from export columns + data rows.
const buildCsv = (exportColumns, dataRows) => {
  const header = exportColumns.map((c) => escapeCsvCell(c.label ?? c.field)).join(",");
  const lines = dataRows.map((row) =>
    exportColumns
      .map((c) => {
        const raw = row?.[c.field];
        const val = c.exportValue ? c.exportValue(raw, row) : raw;
        return escapeCsvCell(val);
      })
      .join(",")
  );
  return [header, ...lines].join("\r\n");
};

// Triggers a client-side download of the CSV (UTF-8 BOM so Excel keeps accents).
const triggerCsvDownload = (csv, fileName) => {
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Local YYYY-MM-DD stamp appended to the exported file name.
const csvDateStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Dynamic row height so multi-line cells (e.g. two phones) are not clipped
const autoRowHeight = () => "auto";

// Zebra: row 0 = light, row 1 = tinted (matches the mock, i % 2)
const zebraClass = (params) =>
  params.indexRelativeToCurrentPage % 2 === 0 ? "rg-row--odd" : "rg-row--even";

// Self-contained width detection (does not depend on MobileProvider).
export const useIsNarrow = (breakpoint) => {
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const update = () => setIsNarrow(window.innerWidth <= breakpoint);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [breakpoint]);

  return isNarrow;
};

// DataGridPro lays out its columns from the measured container width. If it
// mounts (or re-appears) while that width is 0 — inside a tab that becomes
// `display:none` when inactive, or a card that lays out after the first
// paint — flex columns collapse and headers get clipped. We track the live
// container width and only render the grid while it has one, so leaving and
// returning to a tab remounts the grid with a correct, fresh measurement.
const useHasWidth = () => {
  const [node, setNode] = useState(null);
  const [hasWidth, setHasWidth] = useState(false);

  useLayoutEffect(() => {
    if (!node) return undefined;
    // Environments without ResizeObserver (old jsdom in tests, very old
    // browsers): render the grid instead of leaving it hidden.
    if (typeof ResizeObserver === "undefined") {
      setHasWidth(true);
      return undefined;
    }
    const update = () => setHasWidth(node.offsetWidth > 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, hasWidth];
};

/**
 * ResponsiveGrid
 * --------------------------------------------------------------------------
 * Takes ONE column config + the data source and resolves both modes:
 *   - Desktop -> advanced grid (MuiTable / DataGridPro)
 *   - Mobile  -> automatically breaks into cards
 *
 * Column shape (`columns`):
 * {
 *   field:          string   // data key on the row (required)
 *   label:          string   // header text (default: field)
 *   width:          number   // fixed width in px (otherwise uses flex)
 *   flex:           number   // grow factor (default: 1)
 *   minWidth:       number   // minimum width in px
 *   maxWidth:       number   // maximum width in px (caps a flex column)
 *   align:          "left" | "center" | "right"
 *   render:         (value, row) => ReactNode   // custom cell content
 *   icon:           ReactNode  // icon shown next to the field on the card
 *   primary:        boolean    // use this column as the card title
 *   sortable:       boolean    // default: true (desktop)
 *   sortComparator: (a, b) => number   // custom sort (mobile)
 *   defaultSort:    "asc" | "desc"     // marks this column as the initial sort
 *   pinned:         "left" | "right"   // pins the column to that edge (desktop)
 *   visible:        boolean    // when false, drops the column everywhere
 *   hideOnCard:     boolean    // hidden in card mode
 *   hideOnGrid:     boolean    // hidden in the desktop grid
 * }
 *
 * Initial sort:
 *   - via prop -> defaultSort={{ field: "dateCreated", sort: "desc" }}
 *   - or on the column -> { field: "dateCreated", defaultSort: "desc" }
 *
 * Grouping (desktop -> parent row + detail panel; mobile -> sections):
 *   groupBy={{
 *     field: "property",
 *     label: "Property",
 *     summary: [{ label: "Messages", value: (items) => items.length }],
 *   }}
 * Uses the detail panel (Pro feature), NOT the native rowGrouping (Premium).
 */

const isEmptyValue = (v) => v === null || v === undefined || v === "";

// Muted dash shown when a cell has no value
const EmptyDash = () => <span className="rg-dash">—</span>;

// Cell content: custom render, muted dash when empty, or the raw value
const resolveValue = (col, row) => {
  const raw = row?.[col.field];
  if (col.render) return col.render(raw, row);
  return isEmptyValue(raw) ? <EmptyDash /> : raw;
};

const displayValue = (v) => (isEmptyValue(v) ? <EmptyDash /> : v);

// Server-side pagination (numbered pages, next and last). `compact` keeps the
// pager on a single line on small screens by showing fewer page buttons
// (antd renders current ±1 instead of ±2).
const renderServerPagination = (sp, compact = false) => (
  <Pagination
    className="rg-pagination"
    current={sp.page}
    pageSize={sp.pageSize}
    total={sp.total}
    showSizeChanger
    showLessItems={compact}
    pageSizeOptions={sp.pageSizeOptions}
    onChange={(p, ps) => sp.onChange?.(p, ps)}
  />
);

// Generic comparator used to sort the mobile cards
const defaultComparator = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

// Groups rows by `field`, sorting each group's items when requested
const buildGroups = (rows, field, sortItems) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = row?.[field] ?? "(empty)";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  const groups = [...map.entries()].map(([key, items]) => ({
    id: `__group-${key}`,
    __groupKey: key,
    __items: sortItems ? sortItems(items) : items,
  }));
  groups.sort((a, b) =>
    String(a.__groupKey).localeCompare(String(b.__groupKey), undefined, {
      numeric: true,
    })
  );
  return groups;
};

// Item table rendered inside each group's detail panel (desktop)
const GroupDetailTable = memo(({ columns, items, onRowClick }) => {
  const cols = columns.filter((c) => !c.hideOnGrid);
  const clickable = typeof onRowClick === "function";

  return (
    <div className="rg-detail">
      <table className="rg-detail__table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.field} style={{ textAlign: c.align || "left" }}>
                {c.label ?? c.field}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              key={i}
              className={clickable ? "rg-detail__row--clickable" : undefined}
              onClick={clickable ? () => onRowClick(item) : undefined}
            >
              {cols.map((c) => (
                <td key={c.field} style={{ textAlign: c.align || "left" }}>
                  {resolveValue(c, item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

GroupDetailTable.displayName = "ResponsiveGridGroupDetail";

// Single card (mobile mode)
const ResponsiveCard = memo(({ row, columns, onClick }) => {
  const primaryCol =
    columns.find((c) => c.primary && !c.hideOnCard) ||
    columns.find((c) => !c.hideOnCard);

  const fieldCols = columns.filter((c) => !c.hideOnCard && c !== primaryCol);

  const isClickable = typeof onClick === "function";

  return (
    <div
      className="rg-card"
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {primaryCol && (
        <div className="rg-card__header">
          {primaryCol.icon && (
            <div className="rg-card__icon-wrap">{primaryCol.icon}</div>
          )}
          <div className="rg-card__header-text">
            <div className="rg-card__label">
              {primaryCol.label ?? primaryCol.field}
            </div>
            <div className="rg-card__title">{resolveValue(primaryCol, row)}</div>
          </div>
        </div>
      )}

      <div className="rg-card__body">
        {fieldCols.map((col) => {
          const value = resolveValue(col, row);
          return (
            <div
              className={`rg-card__field rg-card__field--${col.field}`}
              key={col.field}
            >
              {col.icon && (
                <div className="rg-card__icon-wrap rg-card__icon-wrap--sm">
                  {col.icon}
                </div>
              )}
              <div className="rg-card__field-text">
                <div className="rg-card__label">{col.label ?? col.field}</div>
                <div className="rg-card__value">{value}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

ResponsiveCard.displayName = "ResponsiveGridCard";

const ResponsiveGrid = ({
  columns: columnsProp = [],
  rows = [],
  getRowId = undefined,
  onRowClick = undefined,
  defaultSort = undefined,
  groupBy = undefined,
  breakpoint = 750,
  // Standard empty-state defaults for every grid: "No data." + inbox icon
  // (the icon default is applied in renderEmpty so the prop type stays a
  // permissive ReactNode). Pass emptyText / emptyIcon to override per screen.
  emptyText = "No data.",
  emptyIcon = undefined,
  emptyDescription = undefined,
  emptyContent = undefined,
  // Server-side (opt-in): a single object with the backend-controlled
  // pagination + sorting. See the `server` PropTypes for its shape.
  server = undefined,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  // CSV export (opt-in): pass a config object to show an "Export CSV" button.
  // See the `exportCsv` PropTypes for its shape.
  exportCsv = undefined,
  loading = false,
  ...muiProps
}) => {
  const isMobile = useIsNarrow(breakpoint);
  const [gridWrapRef, hasWidth] = useHasWidth();
  const [exporting, setExporting] = useState(false);
  const rowId = getRowId ?? ((row) => row?.id);

  // Columns with `visible: false` are dropped everywhere (grid + card).
  const columns = useMemo(
    () => columnsProp.filter((c) => c.visible !== false),
    [columnsProp]
  );

  // CSV export. Rows come from exportCsv.getRows() when provided (which may be
  // async — e.g. paging a server-side endpoint), otherwise from the current
  // `rows`. Columns default to the visible ones minus any `exportable: false`.
  const handleExport = useCallback(async () => {
    if (!exportCsv) return;
    try {
      setExporting(true);
      const dataRows = exportCsv.getRows ? await exportCsv.getRows() : rows;
      const exportColumns = (exportCsv.columns ?? columns).filter(
        (c) => c.exportable !== false
      );
      const csv = buildCsv(exportColumns, dataRows || []);
      const base =
        (typeof exportCsv.fileName === "function"
          ? exportCsv.fileName()
          : exportCsv.fileName) || "export";
      triggerCsvDownload(csv, `${base}-${csvDateStamp()}.csv`);
    } catch (err) {
      console.error("CSV export failed:", err);
      exportCsv.onError?.(err);
    } finally {
      setExporting(false);
    }
  }, [exportCsv, rows, columns]);

  const renderExportBar = () =>
    exportCsv ? (
      <div className="rg-toolbar" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Button
          data-testid="rg-export-csv-btn"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
        >
          {exportCsv.label ?? "Export CSV"}
        </Button>
      </div>
    ) : null;

  const hasServer = !!server;
  const isServerSort = hasServer;

  // Pager config (footer + mobile) derived from `server`
  const serverPagination = hasServer
    ? {
        page: server.page || 1,
        pageSize: server.pageSize || pageSizeOptions[0],
        total: server.total || 0,
        pageSizeOptions,
        onChange: server.onChange,
      }
    : null;
  const hasServerPagination = hasServer;

  // Props forwarded to the DataGrid for server-side pagination/sorting
  const serverGridProps = hasServer
    ? {
        paginationMode: "server",
        sortingMode: "server",
        rowCount: server.total || 0,
        paginationModel: {
          page: (server.page || 1) - 1,
          pageSize: server.pageSize || pageSizeOptions[0],
        },
        onPaginationModelChange: (m) => server.onChange?.(m.page + 1, m.pageSize),
        sortModel: server.sortModel,
        onSortModelChange: server.onSortChange,
        pageSizeOptions,
      }
    : {};

  // Keep the pagination data current for the stable footer slot
  const serverPaginationRef = useRef(serverPagination);
  serverPaginationRef.current = serverPagination;

  // Stable footer component: renders the pager inside the DataGrid footer
  // container, keeping it visually contained in the grid.
  const FooterPager = useCallback(() => {
    const sp = serverPaginationRef.current;
    if (!sp) return null;
    return <div className="rg-grid-pager">{renderServerPagination(sp)}</div>;
  }, []);

  // Empty state (icon + title + description). Standardized default of
  // "No data." + inbox icon; `emptyContent` overrides all, and
  // emptyIcon/emptyText/emptyDescription override the individual parts.
  const renderEmpty = () => {
    if (emptyContent) {
      return <div className="rg-empty">{emptyContent}</div>;
    }

    const icon = emptyIcon ?? <InboxOutlined />;
    const descLines = Array.isArray(emptyDescription)
      ? emptyDescription
      : emptyDescription != null
      ? [emptyDescription]
      : [];

    return (
      <div className="rg-empty">
        {icon && <div className="rg-empty__icon">{icon}</div>}
        {emptyText && <div className="rg-empty__text">{emptyText}</div>}
        {descLines.map((line, i) => (
          <div className="rg-empty__desc" key={i}>
            {line}
          </div>
        ))}
      </div>
    );
  };

  // DataGrid slots: no-rows overlay + optional footer pager
  const gridSlots = useMemo(
    () => ({
      noRowsOverlay: renderEmpty,
      ...(hasServerPagination ? { pagination: FooterPager } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emptyIcon, emptyText, emptyDescription, emptyContent, hasServerPagination, FooterPager]
  );

  // Initial sort: prop wins; otherwise look for a column marked defaultSort
  const sortConfig = useMemo(() => {
    if (defaultSort?.field) {
      return { field: defaultSort.field, sort: defaultSort.sort ?? "asc" };
    }
    const col = columns.find((c) => c.defaultSort);
    return col ? { field: col.field, sort: col.defaultSort } : null;
  }, [defaultSort, columns]);

  // Sorts an array of items by sortConfig (used within groups)
  const sortItems = useCallback(
    (items) => {
      if (!sortConfig?.field) return items;
      const col = columns.find((c) => c.field === sortConfig.field);
      const comparator = col?.sortComparator ?? defaultComparator;
      const dir = sortConfig.sort === "desc" ? -1 : 1;
      return [...items].sort(
        (a, b) =>
          dir * comparator(a?.[sortConfig.field], b?.[sortConfig.field], a, b)
      );
    },
    [columns, sortConfig]
  );

  const groups = useMemo(
    () => (groupBy?.field ? buildGroups(rows, groupBy.field, sortItems) : null),
    [groupBy, rows, sortItems]
  );

  // Parent-row columns (group key + summaries)
  const groupColumns = useMemo(() => {
    if (!groupBy?.field) return null;
    const summary = groupBy.summary ?? [];
    return [
      {
        field: "__groupKey",
        headerName: groupBy.label ?? groupBy.field,
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) => row.__groupKey,
      },
      ...summary.map((s, i) => ({
        field: `__summary_${i}`,
        headerName: s.label,
        align: s.align,
        headerAlign: s.align,
        sortable: false,
        ...(typeof s.width === "number" ? { width: s.width } : { flex: 1 }),
        renderCell: (params) => s.value(params.row.__items),
      })),
    ];
  }, [groupBy]);

  const getGroupDetailPanel = useCallback(
    ({ row }) => (
      <GroupDetailTable
        columns={columns}
        items={row.__items}
        onRowClick={onRowClick}
      />
    ),
    [columns, onRowClick]
  );

  // Column config -> DataGrid format (desktop)
  const gridColumns = useMemo(
    () =>
      columns
        .filter((col) => !col.hideOnGrid)
        .map((col) => ({
          field: col.field,
          headerName: col.label ?? col.field,
          align: col.align,
          headerAlign: col.align,
          sortable: col.sortable !== false,
          ...(typeof col.width === "number"
            ? { width: col.width }
            : { flex: col.flex ?? 1 }),
          ...(col.minWidth ? { minWidth: col.minWidth } : {}),
          ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
          renderCell: (params) => {
            if (col.render) return col.render(params.value, params.row);
            const raw = params.row?.[col.field];
            if (isEmptyValue(raw)) return <EmptyDash />;
            // Text with ellipsis + native tooltip (full content on hover)
            return (
              <span className="rg-cell-ellipsis" title={String(raw)}>
                {raw}
              </span>
            );
          },
        })),
    [columns]
  );

  // Sorted rows for card mode. Skipped in server-side mode: rows already
  // arrive sorted/paginated from the backend.
  const sortedRows = useMemo(() => {
    if (isServerSort || !sortConfig?.field) return rows;
    const col = columns.find((c) => c.field === sortConfig.field);
    const comparator = col?.sortComparator ?? defaultComparator;
    const dir = sortConfig.sort === "desc" ? -1 : 1;
    return [...rows].sort(
      (a, b) => dir * comparator(a?.[sortConfig.field], b?.[sortConfig.field], a, b)
    );
  }, [rows, columns, sortConfig, isServerSort]);

  // Pinned columns declared via `pinned: "left" | "right"`
  const pinnedColumns = useMemo(() => {
    const left = columns.filter((c) => c.pinned === "left").map((c) => c.field);
    const right = columns.filter((c) => c.pinned === "right").map((c) => c.field);
    return left.length || right.length ? { left, right } : null;
  }, [columns]);

  // initialState for the DataGrid (sort + pinning). In server-side mode the
  // sort is controlled (sortModel via props), so we skip initialState.sorting.
  const initialState = useMemo(() => {
    const state = {};
    if (sortConfig?.field && !isServerSort) {
      state.sorting = {
        sortModel: [{ field: sortConfig.field, sort: sortConfig.sort }],
      };
    }
    if (pinnedColumns) state.pinnedColumns = pinnedColumns;
    return Object.keys(state).length ? state : undefined;
  }, [sortConfig, pinnedColumns, isServerSort]);

  // Card-mode pager (mobile). On desktop the pager goes in the grid footer.
  const renderMobilePager = () =>
    serverPagination ? (
      <div className="rg-card-pager">
        {renderServerPagination(serverPagination, true)}
      </div>
    ) : null;

  // Mobile
  if (isMobile) {
    // While loading (initial fetch or refresh after changing the selected
    // community/filters), show placeholder cards instead of stale rows or a
    // flash of the empty state — matches the desktop skeleton behavior.
    if (loading) {
      return (
        <>
          {renderExportBar()}
          <CardSkeletonOverlay />
        </>
      );
    }

    // Grouped -> sections (group header + cards below)
    if (groups) {
      if (!groups.length) {
        return (
          <>
            {renderExportBar()}
            {renderEmpty()}
          </>
        );
      }
      return (
        <>
        {renderExportBar()}
        <div className="rg-group-stack">
          {groups.map((g) => (
            <div className="rg-group" key={g.id}>
              <div className="rg-group__header">
                <span className="rg-group__title">{displayValue(g.__groupKey)}</span>
                <span className="rg-group__count">{g.__items.length}</span>
              </div>
              <div className="rg-card-stack">
                {g.__items.map((item) => (
                  <ResponsiveCard
                    key={rowId(item)}
                    row={item}
                    columns={columns}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        </>
      );
    }

    if (!sortedRows.length) {
      return (
        <>
          {renderExportBar()}
          {renderEmpty()}
        </>
      );
    }
    return (
      <>
        {renderExportBar()}
        <div className="rg-card-stack">
          {sortedRows.map((row) => (
            <ResponsiveCard
              key={rowId(row)}
              row={row}
              columns={columns}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            />
          ))}
        </div>
        {renderMobilePager()}
      </>
    );
  }

  // Desktop, grouped -> parent row per group + detail panel with the items
  if (groups) {
    return (
      <>
        {renderExportBar()}
        <div className="rg-grid" ref={gridWrapRef}>
          {hasWidth && (
            <MuiTable
              rows={groups}
              columnOrder={groupColumns}
              getRowId={(row) => row.id}
              getDetailPanelContent={getGroupDetailPanel}
              slots={gridSlots}
              getRowHeight={autoRowHeight}
              disableRowSelectionOnClick
              loading={loading}
              {...muiProps}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {renderExportBar()}
      <div className="rg-grid" ref={gridWrapRef}>
        {hasWidth && (
          <MuiTable
            rows={rows}
            columnOrder={gridColumns}
            getRowId={rowId}
            onRowClick={onRowClick ? (params) => onRowClick(params.row) : undefined}
            initialState={initialState}
            slots={gridSlots}
            getRowClassName={zebraClass}
            getRowHeight={autoRowHeight}
            disableRowSelectionOnClick
            loading={loading}
            {...serverGridProps}
            {...muiProps}
          />
        )}
      </div>
    </>
  );
};

const columnShape = PropTypes.shape({
  field: PropTypes.string.isRequired,
  label: PropTypes.string,
  width: PropTypes.number,
  flex: PropTypes.number,
  minWidth: PropTypes.number,
  maxWidth: PropTypes.number,
  align: PropTypes.oneOf(["left", "center", "right"]),
  render: PropTypes.func,
  icon: PropTypes.node,
  primary: PropTypes.bool,
  sortable: PropTypes.bool,
  sortComparator: PropTypes.func,
  defaultSort: PropTypes.oneOf(["asc", "desc"]),
  pinned: PropTypes.oneOf(["left", "right"]),
  visible: PropTypes.bool,
  hideOnCard: PropTypes.bool,
  hideOnGrid: PropTypes.bool,
  // CSV export: plain value for the cell (render returns JSX, unusable in CSV);
  // exportable: false drops the column from the export.
  exportValue: PropTypes.func,
  exportable: PropTypes.bool,
});

ResponsiveGrid.propTypes = {
  columns: PropTypes.arrayOf(columnShape).isRequired,
  rows: PropTypes.array.isRequired,
  getRowId: PropTypes.func,
  onRowClick: PropTypes.func,
  defaultSort: PropTypes.shape({
    field: PropTypes.string.isRequired,
    sort: PropTypes.oneOf(["asc", "desc"]),
  }),
  groupBy: PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.string,
    summary: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.func.isRequired,
        align: PropTypes.oneOf(["left", "center", "right"]),
        width: PropTypes.number,
      })
    ),
  }),
  breakpoint: PropTypes.number,
  emptyText: PropTypes.node,
  emptyIcon: PropTypes.node,
  emptyDescription: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  emptyContent: PropTypes.node,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  // Server-side pagination + sorting in a single object.
  server: PropTypes.shape({
    page: PropTypes.number, // 1-based
    pageSize: PropTypes.number,
    total: PropTypes.number,
    sortModel: PropTypes.array, // MUI sort model: [{ field, sort }]
    onSortChange: PropTypes.func, // (sortModel) => void
    onChange: PropTypes.func, // (page, pageSize) => void
  }),
  // CSV export (opt-in). When present, an "Export CSV" button is shown.
  loading: PropTypes.bool,
  exportCsv: PropTypes.shape({
    // Base file name (string or () => string). Date is appended automatically.
    fileName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    // Button label (default "Export CSV").
    label: PropTypes.string,
    // Rows to export; may be async (e.g. page a server-side endpoint).
    // Defaults to the current `rows` when omitted.
    getRows: PropTypes.func,
    // Column set to export; defaults to the visible columns minus exportable:false.
    columns: PropTypes.array,
    // Called with the error if export fails.
    onError: PropTypes.func,
  }),
};

ResponsiveCard.propTypes = {
  row: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(columnShape).isRequired,
  onClick: PropTypes.func,
};

export default ResponsiveGrid;
